<template>
  <div>
    <h2 class="mb-3">{{ action }} a Team Member</h2>

    <!-- User Details & Notification Options -->
    <div class="row align-items-stretch">
        <div class="col mb-3 d-flex flex-column">
            <!-- User Details -->
            <div class="card p-3 flex-grow-1 d-flex flex-column">
                <h3>User Details</h3>
                <div class="row mb-2">
                    <div class="col-6">
                        <label class="form-label">First Name *</label>
                        <input type="text" class="form-control" :class="{'is-invalid': errors.first_name !== ''}"
                            name="first_name" v-model="user_info.first_name" aria-label="First Name *">
                        <div class="text-danger invalid-feedback" v-if="errors.first_name !== ''">
                            {{errors.first_name}}
                        </div>
                    </div>

                    <div class="col-6">
                        <label class="form-label">Last Name *</label>
                        <input type="text" class="form-control" :class="{'is-invalid': errors.last_name !== ''}"
                            name="last_name" v-model="user_info.last_name" aria-label="Last Name *">
                        <div class="text-danger invalid-feedback" v-if="errors.last_name !== ''">
                            {{errors.last_name}}
                        </div>
                    </div>
                </div>

                <div class="row mb-2">
                    <div class="col-12">
                        <label class="form-label">Email *</label>
                        <input type="text" class="form-control" :class="{'is-invalid': errors.email !== ''}"
                            name="email" v-model="user_info.email" :disabled="action === 'Update'" aria-label="Email *">
                        <div class="text-danger invalid-feedback" v-if="errors.email !== ''">
                            {{errors.email}}
                        </div>
                    </div>
                </div>

                <div class="row mb-2" v-if="action === 'Update' && (user.id == user_logged_in.id || user_logged_in.user_type == 'admin')">
                    <div class="col-12">
                        <label class="form-label">Password *</label>
                        <input type="password" class="form-control" :class="{'is-invalid': errors.password !== ''}"
                            name="password" v-model="user_info.password" aria-label="Password *">
                        <div class="text-danger invalid-feedback" v-if="errors.password !== ''">
                            {{errors.password}}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-6 mb-3 d-flex flex-column" v-if="this.user_info.id < 0 || this.user_logged_in.user_type == 'admin'">
            <!-- User Notification Options -->
            <div class="card p-3 flex-grow-1 d-flex flex-column">
                <h3>Email Notification Options</h3>
                <div v-if="notificationLoading">
                <div class="d-flex align-items-center mt-3">
                    <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status">
                    <span class="visually-hidden">Loading...</span>
                    </div>
                    Loading Notification Options
                </div>
                </div>
                <div class="row mb-2" v-for="n in user_notification" :key="n.name">
                    <div class="col">
                        <div class="form-check form-switch d-inline-flex align-items-center gap-2 my-0">
                            <input 
                                class="form-check-input mt-0 me-2" 
                                type="checkbox" 
                                role="switch" 
                                v-model="n.active"
                                @update:modelValue="n.active = !n.active"
                             aria-label="Checkbox">
                            <div>
                                <h6 class="mb-0">{{n.name}}</h6>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-12 mb-3">
            <!-- User Access -->
            <permission-table 
                :user_logged_in="user_logged_in"
                :initialPermissions="user.access"
                ref="userAccess"
                @permission-change="handlePermissionChange"
            />
        </div>
    </div>

    <!-- Error Alert -->
    <div v-if="form.error" class="alert alert-danger mt-3">
      There was an error saving the user information.
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end gap-2 mb-4">
      <button :disabled="form.busy" @click="update" class="btn btn-primary px-4 me-3">
        <span v-if="form.busy" class="d-inline-block spinner-border spinner-border-sm ms-2" role="status">
          <span class="visually-hidden">Loading...</span>
        </span>
        {{ action }}
      </button>

      <button @click="cancel" class="btn btn-white px-4" :disabled="form.busy">Cancel</button>
    </div>
  </div>
</template>

<script>
import { Tooltip } from 'bootstrap';

export default {
  props: {
    client: Object,
    user: Object,
    user_logged_in: Object,
    mode: { type: String, default: 'add' } // 'add' or 'edit'
  },
  data() {
    return {
        action: this.mode === 'edit' ? 'Update' : 'Add',
        user_info: {
            id: this.user?.id || -1,
            first_name: this.user?.first_name || '',
            last_name: this.user?.last_name || '',
            email: this.user?.email || '',
            client_id: this.client.id,
            organization: this.user?.organization || '',
            password: '',
            agency_id: this.user?.agency_id || null
        },
        errors: {
            first_name: '',
            last_name: '',
            email: '',
            password: ''
        },
        form: {
            successful: false,
            busy: false,
            error: false
        },
        user_notification: [],
        notificationLoading: false
    };
  },
  beforeMount() {
    this.getNotificationOptions();
    if (this.user.id && this.user.id > 0) {
        this.action = 'Update';
    }
  },
  updated() {
    // Optionally, re-initialize tooltips on every update
    this.$nextTick(() => {
      this.initTooltips();
    });
  },
  methods: {
    getNotificationOptions() {
        this.notificationLoading = true;
        const params = {};
        if (this.user_info.id && this.user_info.id > 0) {
            params.user_id = this.user_info.id;
        }
        window.axios.get('/api/notification/'+this.client.url+'/options', { params })
        .then(response => {
            //Convert notifications to an array of objects with key = name and value = active
            this.user_notification = Object.keys(response.data.notifications).map(key => ({
                name: key,
                active: response.data.notifications[key]
            }));
            this.notificationLoading = false;
            this.$nextTick(() => {
                this.initTooltips();
            });
        });
    },
    handlePermissionChange({ section, functionality, value }) {
        // When view is disabled, disable the corresponding notification categories
        if (functionality === 'view' && !value) {
            const notificationCategories = this.getNotificationCategoriesForSection(section);
            notificationCategories.forEach(category => {
                const notification = this.user_notification.find(n => n.name === category);
                if (notification) {
                    notification.active = false;
                }
            });
        }
    },
    getNotificationCategoriesForSection(section) {
        // Map permission sections to notification categories
        const mapping = {
            'insights': ['Insights'],
            'connections': ['Integrations', 'Files'],
            'billing': ['Billing']
        };
        return mapping[section] || [];
    },
    initTooltips() {
        // Remove any existing tooltips to avoid duplicates
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.forEach(el => {
            if (el._tooltipInstance) {
                el._tooltipInstance.dispose();
            }
            el._tooltipInstance = new Tooltip(el);
        });
    },
    clearErrors() { //reset errors to empty strings
        this.errors = {
            first_name: '',
            last_name: '',
            email: '',
            password: ''
        };
    },
    hasErrors() { //validate the form
        if(this.user_info.first_name == null || this.user_info.first_name == ""){
            this.errors.first_name = "First Name is required.";
        } else
            this.errors.first_name = "";

        if(this.user_info.last_name == null || this.user_info.last_name == ""){
            this.errors.last_name = "Last Name is required.";
        } else
            this.errors.last_name = "";

        if(this.user_info.email == null || this.user_info.email == ""){
            this.errors.email = "Email is required.";
        } else
            this.errors.email = "";

        if(this.action == 'Update' && this.user_info.password != "" &&
            this.user_info.password.length < 8){
            this.errors.password = "The password must be at least 8 characters long. Leave it blank if you'd like to not change it.";
        } else
            this.errors.password = "";

        if (this.errors.first_name!="" || this.errors.last_name!="" || this.errors.email!="" || this.errors.password!=""){
            return true
        } else{
            return false
        }
    },
    update() {
        this.clearErrors();
        if(this.hasErrors()) {
            return;
        }
        this.form.busy = true;
        this.user_info.access = this.$refs.userAccess.getPermissions();

        if(this.user_info.id < 0) { // add user
            var prefs = this.user_notification;
            this.user_info.notification_preference = Object.fromEntries(prefs.map(n => [n.name, n.active]));
            window.axios.post('/api/client/add_team_user', this.user_info)
                .then(response => {

                    Swal.fire({
                        html: '<h3 class="mb-0">' + this.user_info.first_name + ' was added!</h3>',
                        target: '#org_settings_team',
                        icon: 'success',
                        customClass: {
                            popup: 'swal-success'
                        },
                    }).then(() => {
                        // Redirect to team screen after Swal closes
                        window.location.href = '/settings/' + this.client.url + '/team';
                    });


                }).catch(error=> {
                    this.form.busy = false;
                    this.form.error = true;
                    if (error.response?.data?.message) {
                        this.errors.email = error.response.data.message;
                    } else {
                        this.errors.email = 'An error occurred while adding the user. Please try again.';
                    }
                    setTimeout(() => {this.form.error = false}, 10000);
                });
        }
        else {
            //Update a user
            var prefs = this.user_notification;
            this.user_info.notification_preference = Object.fromEntries(prefs.map(n => [n.name, n.active]));
            window.axios.put('/api/user/' +this.user_info.id, this.user_info)
                .then(response => {

                Swal.fire({
                    html: '<h3 class="mb-0">' + this.user_info.first_name + ' was updated!</h3>',
                    target: '#org_settings_team',
                    icon: 'success',
                    customClass: {
                        popup: 'swal-success'
                    },
                }).then(() => {
                    // Redirect to team screen after Swal closes
                    window.location.href = '/settings/' + this.client.url + '/team';
                });
            }).catch(error => {
                this.form.busy = false;
                // Handle errors here
                if (error.response?.status === 422) {
                    this.errors.email = 'The email entered is not in the correct format.';
                } else if (error.response?.data?.message) {
                    this.errors.email = error.response.data.message;
                } else {
                    this.errors.email = 'An error occurred while updating the user. Please try again.';
                    this.form.error = true;
                    setTimeout(() => {this.form.error = false}, 10000);
                }
            });
        }
    },
    cancel() {
      window.location.href = '/settings/' + this.client.url + '/team';
    },
  },
};
</script>

